import { CellTable, SortOrder } from "@/atoms"
import { CELLS, getDefaultColumnParams } from "@/helpers/table.helpers"
import { RowClick } from "@/hooks/useLinkDrawerTooltip.ts"
import { NumberConvertor } from "@/hooks/useNumberConvertor"
import { ORDER_BY_PROPS } from "@/hooks/useOrderBy"
import { TFunction } from "@/hooks/useTranslation"
import { HeaderCell } from "@/molecules/HeaderCell"
import { ApplicationNameCell } from "@/pages/DiscoveryPage/components/molecules/ApplicationNameCell"
import { ApplicationStatusCell } from "@/pages/DiscoveryPage/components/molecules/ApplicationStatusCell"
import { ApplicationStatusPopover } from "@/pages/DiscoveryPage/components/molecules/ApplicationStatusPopover"
import { InsightsApplicationCell } from "@/pages/DiscoveryPage/components/molecules/InsightsApplicationCell"
import { RelatedImagesCell } from "@/pages/DiscoveryPage/components/molecules/RelatedImagesCell"

import type { ColumnType } from "@/atoms"
import type { Application } from "@/pages/DiscoveryPage/application/application-inventory/useApplicationInventory.types"

export const getColumnsTablePreviewMode = (
  sorter: Omit<ORDER_BY_PROPS, "onSorterChange">,
  cells: ColumnType<Application>[] = []
) => [
  {
    title: "Application Name",
    render: ApplicationNameCell,
    ...getDefaultColumnParams("displayName", sorter),
    sortDirections: ["descend", "ascend", "descend"] as SortOrder[]
  },
  ...cells,
  {
    title: "Environment/Namespace",
    width: 200,
    render: CellTable,
    ...getDefaultColumnParams("environmentAndNamespace")
  }
]

export const getColumnsTable = (props: {
  internetFacingFF?: boolean
  previewMode?: boolean
  convertors: NumberConvertor
  sorter: Omit<ORDER_BY_PROPS, "onSorterChange">
  t: TFunction
  onRowClick: RowClick
}): ColumnType<Application>[] => {
  const { internetFacingFF, previewMode, convertors, sorter, t, onRowClick } =
    props
  const statusColumn = {
    title: HeaderCell({
      title: "Status",
      titlePopover: "Statuses",
      Content: ApplicationStatusPopover
    }),
    width: 100,
    render: ApplicationStatusCell(props),
    ...getDefaultColumnParams("workloadStatus")
  }
  const columnsPreviewMode = getColumnsTablePreviewMode(sorter, [statusColumn])

  if (previewMode) return columnsPreviewMode

  return [
    ...columnsPreviewMode,
    {
      title: "Type",
      width: 120,
      render: (value) =>
        t(`inventory.application.workloadKind.${value}`, {
          defaultValue: t("inventory.application.workloadKind.unknown")
        }),
      ...getDefaultColumnParams("workloadKind")
    },
    {
      title: "Related Images",
      width: "20%",
      render: RelatedImagesCell(t, onRowClick),
      ...getDefaultColumnParams("relatedImages")
    },
    {
      title: "Insights",
      width: internetFacingFF ? 90 : 75,
      render: InsightsApplicationCell({ t, internetFacingFF }),
      ...getDefaultColumnParams("insights")
    },
    CELLS.getIssuesSummaryCell({ convertors, sorter, t }),
    CELLS.getLastDetectedCell({
      entityName: "application",
      t,
      width: 155,
      ...getDefaultColumnParams("lastDetectionData")
    })
  ]
}
